import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";
import { roleIdToName, normalizeRoleId } from "../../../services/lookupService";
import "../../common/BadgeMultiSelect/BadgeMultiSelect.scss";
import "./MilestoneRecipientRoles.scss";
import {
  computeAnchoredPopoverPosition,
  getAnchoredPopoverStyle,
  useAnchoredPopoverPosition,
} from "./milestoneAnchoredPopover";

const MAX_VISIBLE_AVATARS = 5;

const INITIAL_POPOVER_POSITION = {
  top: 12,
  bottom: null,
  left: 12,
  openUpward: false,
  listMaxHeight: 220,
  width: 248,
};

const RolePickerPopover = ({
  open,
  onClose,
  position,
  title,
  subtitle,
  options,
  selectedRoleIds,
  onSelect,
  popoverRef,
  showSearch = false,
  mode = "toggle",
  emptyMessage = "No roles found",
}) => {
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((role) =>
      String(role?.roleName || "").toLowerCase().includes(term),
    );
  }, [options, search]);

  const selectedRoleIdSet = useMemo(
    () =>
      new Set(
        (Array.isArray(selectedRoleIds) ? selectedRoleIds : [])
          .map((id) => normalizeRoleId(id))
          .filter(Boolean),
      ),
    [selectedRoleIds],
  );

  if (!open) return null;

  const popover = (
    <div
      className="badge-multi-select__popover badge-multi-select__popover--anchored milestone-recipient-roles__popover"
      ref={popoverRef}
      style={getAnchoredPopoverStyle(position)}
    >
      <div className="badge-multi-select__popover-head">
        <div>
          <strong>{title}</strong>
          <span>{subtitle}</span>
        </div>
        <button type="button" onClick={onClose} aria-label={`Close ${title}`}>
          ×
        </button>
      </div>

      {showSearch && (
        <div className="milestone-recipient-roles__search-wrap">
          <input
            type="text"
            className="form-control form-control-sm milestone-recipient-roles__search"
            placeholder="Search roles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
      )}

      <div
        className="badge-multi-select__popover-list"
        style={{ maxHeight: `${position.listMaxHeight}px` }}
      >
        {filteredOptions.length ? (
          filteredOptions.map((role) => {
            const selected = selectedRoleIdSet.has(normalizeRoleId(role.roleId));
            return (
              <button
                key={role.roleId}
                type="button"
                className={selected && mode === "toggle" ? "is-selected" : ""}
                onClick={() => onSelect(role.roleId)}
              >
                <span>{role.roleName}</span>
                {selected && mode === "toggle" ? <small>Added</small> : null}
              </button>
            );
          })
        ) : (
          <div className="milestone-recipient-roles__empty">{emptyMessage}</div>
        )}
      </div>
    </div>
  );

  return createPortal(popover, document.body);
};

const MilestoneRecipientRoles = ({
  selectedRoleIds = [],
  allRoles = [],
  disabled = false,
  onChange,
  showSearch = false,
  excludeSelectedFromPicker = false,
}) => {
  const rootRef = useRef(null);
  const addTriggerRef = useRef(null);
  const overflowTriggerRef = useRef(null);
  const addPopoverRef = useRef(null);
  const overflowPopoverRef = useRef(null);

  const [addOpen, setAddOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [addPosition, setAddPosition] = useState(INITIAL_POPOVER_POSITION);
  const [overflowPosition, setOverflowPosition] = useState(INITIAL_POPOVER_POSITION);

  useAnchoredPopoverPosition(addOpen, addTriggerRef, setAddPosition, { showSearch });
  useAnchoredPopoverPosition(overflowOpen, overflowTriggerRef, setOverflowPosition, {
    showSearch: false,
  });

  const roleIds = useMemo(
    () => (Array.isArray(selectedRoleIds) ? selectedRoleIds : []),
    [selectedRoleIds],
  );
  const roleIdSet = useMemo(
    () => new Set(roleIds.map((id) => normalizeRoleId(id)).filter(Boolean)),
    [roleIds],
  );
  const visibleRoleIds = roleIds.slice(0, MAX_VISIBLE_AVATARS);
  const overflowRoleIds = roleIds.slice(MAX_VISIBLE_AVATARS);

  const availableRoles = useMemo(() => {
    if (!excludeSelectedFromPicker) return allRoles;
    return allRoles.filter(
      (role) => !roleIdSet.has(normalizeRoleId(role.roleId)),
    );
  }, [allRoles, excludeSelectedFromPicker, roleIdSet]);

  useEffect(() => {
    if (!addOpen && !overflowOpen) return undefined;

    const handleMouseDown = (event) => {
      const inAddPopover = addPopoverRef.current?.contains(event.target);
      const inOverflowPopover = overflowPopoverRef.current?.contains(event.target);
      const inAddTrigger = addTriggerRef.current?.contains(event.target);
      const inOverflowTrigger = overflowTriggerRef.current?.contains(event.target);

      if (inAddPopover || inOverflowPopover || inAddTrigger || inOverflowTrigger) {
        return;
      }
      setAddOpen(false);
      setOverflowOpen(false);
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [addOpen, overflowOpen]);

  const addRole = (roleId) => {
    if (disabled || !onChange || roleIdSet.has(normalizeRoleId(roleId))) return;
    onChange([...roleIds, roleId]);
  };

  const toggleRole = (roleId) => {
    if (disabled || !onChange) return;
    if (roleIdSet.has(normalizeRoleId(roleId))) {
      onChange(roleIds.filter((id) => normalizeRoleId(id) !== normalizeRoleId(roleId)));
      return;
    }
    onChange([...roleIds, roleId]);
  };

  const removeRole = (roleId) => {
    if (disabled || !onChange) return;
    onChange(roleIds.filter((id) => normalizeRoleId(id) !== normalizeRoleId(roleId)));
  };

  const openAddPicker = (event) => {
    if (disabled || availableRoles.length === 0) return;
    setOverflowOpen(false);
    setAddPosition(
      computeAnchoredPopoverPosition(event.currentTarget, { showSearch }),
    );
    setAddOpen((open) => !open);
  };

  const openOverflowPicker = (event) => {
    if (disabled) return;
    setAddOpen(false);
    setOverflowPosition(computeAnchoredPopoverPosition(event.currentTarget));
    setOverflowOpen((open) => !open);
  };

  if (disabled && roleIds.length === 0) {
    return <span className="text-muted small">—</span>;
  }

  const renderAvatar = (roleId, { onClick, className = "", title } = {}) => {
    const roleName = roleIdToName(roleId, allRoles);
    const displayName = roleName || "Role";
    const initials = roleName ? getInitials(roleName) : "?";
    const style = { backgroundColor: getAvatarColor(roleName || roleId) };
    const classes = `milestone-recipient-roles__avatar ${className}`.trim();

    if (disabled || !onClick) {
      return (
        <span className={classes} style={style} title={title || displayName}>
          {initials}
        </span>
      );
    }

    return (
      <button
        type="button"
        className={classes}
        style={style}
        title={title || `${displayName} — click to remove`}
        onClick={onClick}
      >
        {initials}
      </button>
    );
  };

  const addMode = excludeSelectedFromPicker ? "add" : "toggle";
  const addOptions = excludeSelectedFromPicker ? availableRoles : allRoles;
  const onAddSelect = excludeSelectedFromPicker ? addRole : toggleRole;

  const overflowOptions = overflowRoleIds
    .map((roleId) => allRoles.find((role) => role.roleId === roleId))
    .filter(Boolean);

  return (
    <div className="milestone-recipient-roles" ref={rootRef}>
      {visibleRoleIds.length > 0 && (
        <div className="milestone-recipient-roles__avatars">
          {visibleRoleIds.map((roleId) => (
            <React.Fragment key={roleId}>
              {renderAvatar(roleId, {
                onClick: disabled ? null : () => removeRole(roleId),
              })}
            </React.Fragment>
          ))}

          {overflowRoleIds.length > 0 && (
            <>
              {disabled ? (
                <span
                  className="milestone-recipient-roles__avatar milestone-recipient-roles__avatar--more"
                  title={overflowRoleIds
                    .map((roleId) => roleIdToName(roleId, allRoles))
                    .join(", ")}
                >
                  +{overflowRoleIds.length}
                </span>
              ) : (
                <button
                  ref={overflowTriggerRef}
                  type="button"
                  className="milestone-recipient-roles__avatar milestone-recipient-roles__avatar--more"
                  title="Show more roles"
                  onClick={openOverflowPicker}
                >
                  +{overflowRoleIds.length}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {!disabled && availableRoles.length > 0 && (
        <button
          ref={addTriggerRef}
          type="button"
          className="badge-multi-select__add milestone-recipient-roles__add"
          onClick={openAddPicker}
        >
          + Add role
        </button>
      )}

      <RolePickerPopover
        open={addOpen}
        onClose={() => setAddOpen(false)}
        position={addPosition}
        title="Add role"
        subtitle={
          excludeSelectedFromPicker
            ? "Select roles to add"
            : "Select roles to notify"
        }
        options={addOptions}
        selectedRoleIds={roleIds}
        onSelect={onAddSelect}
        popoverRef={addPopoverRef}
        showSearch={showSearch}
        mode={addMode}
        emptyMessage={
          excludeSelectedFromPicker
            ? "All roles have been added"
            : "No roles found"
        }
      />

      <RolePickerPopover
        open={overflowOpen && !disabled}
        onClose={() => setOverflowOpen(false)}
        position={overflowPosition}
        title="More roles"
        subtitle="Click a role to remove"
        options={overflowOptions}
        selectedRoleIds={roleIds}
        onSelect={removeRole}
        popoverRef={overflowPopoverRef}
        showSearch={false}
        mode="remove"
        emptyMessage="No roles"
      />
    </div>
  );
};

export default MilestoneRecipientRoles;
